/**
 * propertyFormSchema.js — v2.89 (iter_130)
 * ==========================================
 *
 * SINGLE SOURCE OF TRUTH for the Add/Edit Property form + Property Detail
 * page. Every field visibility, required-field, and section-grouping
 * decision anywhere in the app must derive from this file.
 *
 * Guarantees
 * ----------
 * 1. Field visibility gates are ONE map, keyed by property type.
 * 2. Required-field rules adapt to (property_type, listing_intent) where
 *    listing_intent ∈ {"Sale", "Rent", "Lease"}.
 * 3. Section grouping (Basic Details, Rooms & Area, Floor/Building,
 *    Features & Amenities, Price & Charges, Location, Description,
 *    Documents, Contact, Admin) is defined ONCE — form Review step + the
 *    public Property Detail page render from the SAME section order.
 * 4. Every field carries `store: "top" | "custom"` so form state + doc
 *    read paths are unambiguous.
 * 5. Never silently defaults to Builder Floor — see `getPropertyTypeKey()`
 *    which returns `null` for empty input (was the iter_129 regression).
 *
 * Public API
 * ----------
 *   PROPERTY_TYPE_KEYS         — active canonical keys ordered per policy
 *   FIELD_META                 — id → {label, ui, store, storeKey, options}
 *   SECTION_ORDER              — 9-section order for form Review + detail page
 *   FIELDS_BY_TYPE[key]        — {section → [fieldId, …]}
 *   REQUIRED_FIELDS(type, intent) → Set<fieldId>  (dynamic)
 *   getPropertyTypeKey(input)  → key | null  (aliases resolved; NEVER defaults)
 *   getVisibleFieldIds(type)   → Set<fieldId>
 *
 * Do NOT re-add "Flat" or "Apartment" as keys — final policy (iter_129).
 */

// -----------------------------------------------------------------------
// Property type key registry — must match backend `property_types` master
// -----------------------------------------------------------------------
export const PROPERTY_TYPE_KEYS = [
  "BuilderFloor", "House", "Plot", "Shop", "Office", "Building",
];

// Canonical display name for each key (matches DB `property_type` values)
export const CANONICAL_LABEL = {
  BuilderFloor: "Builder Floor",
  House:        "Independent House",
  Plot:         "Plot",
  Shop:         "Shop",
  Office:       "Office",
  Building:     "Building",
};

// Alias map (lowercase input → key). Includes legacy Apartment/Flat/etc.
// for edit-mode compatibility (any residual raw value gets resolved to
// its post-migration canonical bucket).
const KEY_ALIASES = {
  "builder floor": "BuilderFloor", "builderfloor": "BuilderFloor",
  "flat": "BuilderFloor", "apartment": "BuilderFloor",
  "flat/apartment": "BuilderFloor", "society flat": "BuilderFloor",
  "group housing": "BuilderFloor", "dda flat": "BuilderFloor",
  "cghs flat": "BuilderFloor",
  "independent house": "House", "house": "House", "villa": "House",
  "bungalow": "House", "kothi": "House",
  "plot": "Plot", "land": "Plot", "plot/land": "Plot",
  "shop": "Shop", "retail": "Shop", "showroom": "Shop",
  "office": "Office", "office space": "Office", "workspace": "Office",
  "building": "Building", "full building": "Building",
  "whole building": "Building", "entire building": "Building",
};

/** Never silently defaults. Returns null for empty/unknown input.
 *  This is the iter_130 fix for the iter_129 regression where
 *  getMatrixKey('') returned "BuilderFloor" and leaked field
 *  visibility into the empty state before a user picked a type. */
export function getPropertyTypeKey(input) {
  if (!input || typeof input !== "string") return null;
  const needle = input.trim().toLowerCase();
  if (KEY_ALIASES[needle]) return KEY_ALIASES[needle];
  // Direct key match (any case)
  for (const k of PROPERTY_TYPE_KEYS) {
    if (k.toLowerCase() === needle) return k;
  }
  return null;
}

// -----------------------------------------------------------------------
// FIELD_META — every renderable field in one place
// -----------------------------------------------------------------------
//   ui values: text | textarea | number | money | area | select |
//              boolean | multiselect | file
//   store: "top"   → value lives at form.<storeKey>
//   store: "custom"→ value lives at form.custom_fields[storeKey]
//
//   showInHero      → rendered on the compact Hero specs grid (default false)
//   showInDetails   → rendered inside Property Details sections (default true)
//   formEnabled     → surfaced in the Add/Edit form (default true)
//   readAliases     → optional list of legacy storage keys to fall back
//                     to when the primary storage key is empty. Never
//                     WRITE to an alias; only READ. Used to keep old
//                     documents visible without mutating stored data.
// -----------------------------------------------------------------------
export const FIELD_META = {
  // — Basic (shared)
  listing_type:    { label: "Listing Intent",       ui: "select",  store: "top",    storeKey: "listing_type",
                     options: ["Sale", "Rent", "Lease"] },
  title:           { label: "Listing Title",         ui: "text",    store: "top",    storeKey: "title" },
  property_type:   { label: "Property Type",         ui: "select",  store: "top",    storeKey: "property_type",
                     options: PROPERTY_TYPE_KEYS.map((k) => CANONICAL_LABEL[k]) },
  main_area:       { label: "Main Area",             ui: "text",    store: "top",    storeKey: "main_area" },
  sub_area:        { label: "Sub Area",              ui: "text",    store: "top",    storeKey: "sub_area" },
  landmark:        { label: "Landmark",              ui: "text",    store: "custom", storeKey: "landmark" },
  google_maps_link:{ label: "Map Location",          ui: "text",    store: "top",    storeKey: "google_maps_link" },
  status:          { label: "Publication Status",    ui: "select",  store: "top",    storeKey: "status",
                     options: ["Available", "Under Offer", "Sold"] },
  condition:       { label: "Condition",             ui: "select",  store: "custom", storeKey: "condition",
                     options: ["", "New", "Under Construction", "Ready to Move", "Resale", "Renovated"] },
  description:     { label: "Description",           ui: "textarea", store: "top",   storeKey: "notes" },
  images:          { label: "Photos",                ui: "file",    store: "top",    storeKey: "images" },
  videos:          { label: "Video Tour",            ui: "file",    store: "top",    storeKey: "videos" },
  contact:         { label: "Contact / Assigned Dealer", ui: "text", store: "top",  storeKey: "contact" },

  // — Price & Charges
  price:           { label: "Sale Price",            ui: "money",   store: "top",    storeKey: "price" },
  monthly_rent:    { label: "Monthly Rent",          ui: "money",   store: "top",    storeKey: "monthly_rent" },
  monthly_lease:   { label: "Monthly Lease",         ui: "money",   store: "custom", storeKey: "monthly_lease" },
  security_deposit:{ label: "Security Deposit",      ui: "money",   store: "custom", storeKey: "security_deposit" },
  maintenance:     { label: "Maintenance (₹/mo)",    ui: "money",   store: "custom", storeKey: "maintenance" },
  negotiable:      { label: "Negotiable",            ui: "boolean", store: "custom", storeKey: "negotiable" },

  // — Rooms & Area
  //   v2.91 — `bhk` is shown in Hero AND hidden from the Property Details
  //   Rooms & Area section (per user rule: never duplicate a field). Same
  //   treatment for `built_up_area` (Hero "Built-up Area" is the single
  //   surface for size). `furnishing` and `floor` follow the same rule.
  bhk:             { label: "BHK",                   ui: "select",  store: "top",    storeKey: "bhk",
                     options: ["", "1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK", "6+ BHK", "Studio"],
                     showInHero: true, showInDetails: false },
  bathrooms:       { label: "Bathrooms",             ui: "select",  store: "custom", storeKey: "bathrooms",
                     options: ["", "1", "2", "3", "4", "5+"],
                     readAliases: ["bathroom"] },  // v2.91 — form writes `bathroom`; read both keys
  balconies:       { label: "Balconies",             ui: "select",  store: "custom", storeKey: "balconies",
                     options: ["", "0", "1", "2", "3", "4+"],
                     readAliases: ["balcony"] },   // v2.91 — form writes `balcony`; both legacy AND new records visible
  built_up_area:   { label: "Built-up Area",         ui: "area-top", store: "top",   storeKey: "size_sqft",
                     // v2.91 — the size lives in the user-selected unit
                     // (see `size_unit`). Hero renders this via formatSize
                     // helper. Details page skips it to avoid duplicate row.
                     showInHero: true, showInDetails: false },
  plot_area:       { label: "Plot Area (sqyd)",      ui: "area",    store: "custom", storeKey: "plot_area" },
  plot_dimensions: { label: "Plot Dimensions",       ui: "text",    store: "custom", storeKey: "plot_dimensions",
                     placeholder: "e.g. 30x40 ft" },
  cabins:          { label: "Cabins",                ui: "number",  store: "custom", storeKey: "cabins" },
  workstations:    { label: "Workstations",          ui: "number",  store: "custom", storeKey: "workstations" },
  conference_room: { label: "Conference Room",       ui: "select",  store: "custom", storeKey: "conference_room",
                     options: ["", "Yes", "No"] },
  washroom:        { label: "Washroom",              ui: "select",  store: "custom", storeKey: "washroom",
                     options: ["", "Yes", "No"] },
  washrooms:       { label: "Washrooms",             ui: "number",  store: "custom", storeKey: "washrooms" },
  pantry:          { label: "Pantry",                ui: "select",  store: "custom", storeKey: "pantry",
                     options: ["", "Yes", "No"] },
  total_units:     { label: "Total Units",           ui: "number",  store: "custom", storeKey: "total_units" },

  // — Floor / Building
  current_floor:   { label: "Current Floor",         ui: "select",  store: "custom", storeKey: "current_floor",
                     options: ["", "Basement", "Lower Ground", "Ground", "Upper Ground",
                               "1st Floor", "2nd Floor", "3rd Floor", "4th Floor",
                               "5th Floor", "6th Floor", "7th Floor", "8th Floor",
                               "9th Floor", "10th Floor", "10+ Floor", "Top Floor", "Other"],
                     // v2.91 — legacy free-text `top.floor` was superseded
                     // by this dropdown; some old docs still carry the
                     // free-text value. Read-time fallback keeps them
                     // visible. Alias is a top-level key, marked with a
                     // "top:" prefix so the resolver knows to check it
                     // outside custom_fields.
                     readAliases: ["top:floor"] },
  floors_count:    { label: "Total Floors",          ui: "select",  store: "custom", storeKey: "floors_count",
                     options: ["", "Basement + Ground", "Ground Only",
                               "G+1", "G+2", "G+3", "G+4", "G+5", "G+6",
                               "G+7", "G+8", "G+9", "G+10", "G+10+", "Other"],
                     readAliases: ["total_floors"] },  // v2.91 — old docs used `total_floors`
  lift:            { label: "Lift",                  ui: "select",  store: "custom", storeKey: "lift",
                     options: ["", "Yes", "No"] },
  // v2.91/v2.93 — Canonical Parking field.
  //
  // v2.91 briefly introduced a split parking model (Car + Bike + Type)
  // to match a stakeholder spec. v2.93 collapsed it back to a single
  // select: None / Car / Bike / Car + Bike — matching the Uttam Nagar /
  // Nawada / Dwarka Mor inventory pattern where "Covered vs Stilt" is
  // never actually relevant.
  //
  // legacyOptions covers every value old records may carry so Edit
  // mode never appears "empty" for legacy listings:
  //   • pre-v2.93 vehicle labels ("Car Parking" / "Bike Parking" /
  //     "Car + Bike Parking" / "No Parking")
  //   • pre-v2.83 open labels ("Open" / "Covered" / "Both")
  parking:         { label: "Parking",               ui: "select",  store: "custom", storeKey: "parking",
                     options: ["", "None", "Car", "Bike", "Car + Bike"],
                     legacyOptions: [
                       "No Parking", "Bike Parking", "Car Parking", "Car + Bike Parking",
                       "Open", "Covered", "Both",
                     ] },
  // v2.93 — Legacy split fields kept in FIELD_META for programmatic
  // reads (see resolveParkingSummary) but disabled everywhere in the UI:
  // no form input, no details row. Existing DB values are preserved and
  // resolved into the canonical Parking row on the public page.
  car_parking:     { label: "Car Parking (legacy)", ui: "select",  store: "custom", storeKey: "car_parking",
                     options: ["", "Yes", "No"],
                     formEnabled: false, showInDetails: false },
  bike_parking:    { label: "Bike Parking (legacy)", ui: "select", store: "custom", storeKey: "bike_parking",
                     options: ["", "Yes", "No"],
                     formEnabled: false, showInDetails: false },
  parking_type:    { label: "Parking Type (legacy)", ui: "select", store: "custom", storeKey: "parking_type",
                     options: ["", "Covered", "Stilt"],
                     formEnabled: false, showInDetails: false },
  parking_count:   { label: "Parking Slots",         ui: "number",  store: "custom", storeKey: "parking_count",
                     formEnabled: false, showInDetails: false },   // v2.91 — dropped per user spec, not exposed

  // — Features & Amenities
  furnishing:      { label: "Furnishing",            ui: "select",  store: "top",    storeKey: "furnishing",
                     options: ["Unfurnished", "Semi-Furnished", "Fully Furnished"],
                     showInHero: true, showInDetails: false },
  facing:          { label: "Facing",                ui: "select",  store: "custom", storeKey: "facing",
                     options: ["", "North", "South", "East", "West",
                               "North-East", "North-West", "South-East", "South-West"] },
  // v2.91 — NEW field: property_age (per user spec §9A).
  property_age:    { label: "Property Age",          ui: "select",  store: "custom", storeKey: "property_age",
                     options: ["", "Under Construction", "0-1 Year", "1-5 Years",
                               "5-10 Years", "10-15 Years", "15+ Years"] },
  amenities:       { label: "Amenities",             ui: "multiselect", store: "custom", storeKey: "amenities",
                     options: ["Power Backup", "Security", "Water Storage", "Piped Gas",
                               "Park", "Play Area", "Gym", "Swimming Pool", "Club House",
                               "Vaastu Compliant", "CCTV", "Servant Quarter", "Study Room",
                               "Puja Room", "Store Room"] },
  road_width:      { label: "Road Width (ft)",       ui: "number",  store: "custom", storeKey: "road_width_ft" },
  boundary_wall:   { label: "Boundary Wall",         ui: "select",  store: "custom", storeKey: "boundary_wall",
                     options: ["", "Yes", "No", "Partial"] },
  corner_property: { label: "Corner Property",       ui: "select",  store: "custom", storeKey: "corner",
                     options: ["", "Yes", "No"] },
  approved_authority: { label: "Approved Authority", ui: "text",    store: "custom", storeKey: "approved_authority",
                        placeholder: "e.g. DDA, MCD, RERA" },
  construction_allowed: { label: "Construction Allowed", ui: "select", store: "custom", storeKey: "construction_allowed",
                          options: ["", "Yes", "No", "With Approval"] },
  frontage:        { label: "Frontage (ft)",         ui: "number",  store: "custom", storeKey: "front_width_ft" },
  power_backup:    { label: "Power Backup",          ui: "select",  store: "custom", storeKey: "power_backup",
                     options: ["", "None", "Partial", "Full"] },
  suitable_business_type: { label: "Suitable Business Type", ui: "text", store: "custom", storeKey: "suitable_for",
                            placeholder: "e.g. Restaurant, Clinic, Retail, Salon" },
  commercial_road_type: { label: "Commercial Road / Type", ui: "select", store: "custom", storeKey: "shop_type",
                          options: ["", "Standalone", "Mall", "Strip Mall", "Highway Front",
                                    "Market Complex", "Office Tower", "Co-working"] },
  usage_type:      { label: "Usage Type",            ui: "select",  store: "custom", storeKey: "building_type",
                     options: ["", "Residential", "Commercial", "Mixed-Use", "Industrial"] },

  // — Ownership & Docs
  ownership:       { label: "Ownership",             ui: "select",  store: "custom", storeKey: "registry_type",
                     options: ["", "Freehold", "Leasehold", "Power of Attorney",
                               "Allotment", "Conveyance Deed"] },
  possession:      { label: "Possession",            ui: "select",  store: "custom", storeKey: "possession",
                     options: ["", "Ready to Move", "Within 3 Months", "3-6 Months",
                               "6-12 Months", "1+ Year", "Under Construction"] },
};

// -----------------------------------------------------------------------
// Section order — used by both the form Review step and PropertyDetail
// -----------------------------------------------------------------------
export const SECTION_ORDER = [
  "basic",
  "rooms_area",
  "floor_building",
  "features_amenities",
  "price_charges",
  "location",
  "description",
  "documents",
  "contact",
  "admin",
];

export const SECTION_LABELS = {
  basic:              "Basic Details",
  rooms_area:         "Rooms & Area",
  floor_building:     "Floor / Building Details",
  features_amenities: "Features & Amenities",
  price_charges:      "Price & Charges",
  location:           "Location",
  description:        "Description",
  documents:          "Documents",
  contact:            "Contact",
  admin:              "Admin Information",
};

// -----------------------------------------------------------------------
// FIELDS_BY_TYPE — per-type field visibility, grouped by section.
// Missing keys ⇒ field is HIDDEN for that type.
// -----------------------------------------------------------------------
const COMMON_BASIC     = ["listing_type", "title", "property_type", "condition", "status"];
const COMMON_LOCATION  = ["main_area", "sub_area", "landmark", "google_maps_link"];
const COMMON_MEDIA_DOC = ["images", "videos"];
const COMMON_CONTACT   = ["contact"];
const COMMON_DESC      = ["description"];

export const FIELDS_BY_TYPE = {
  BuilderFloor: {
    basic:              COMMON_BASIC,
    // v2.91 — Removed `carpet_area` (per user directive: keep ONLY built_up_area).
    // Duplicate rows suppressed by field-level showInDetails flag — the schema
    // still declares `bhk` / `built_up_area` here so their metadata is discoverable,
    // but the detail-page renderer skips fields whose showInDetails === false.
    rooms_area:         ["bhk", "bathrooms", "balconies", "built_up_area"],
    floor_building:     ["current_floor", "floors_count", "lift",
                         // v2.93 — Single canonical parking field. Legacy
                         // split fields (car_parking/bike_parking/
                         // parking_type) are resolved at read time via
                         // resolveParkingSummary — they never appear as
                         // separate rows on the detail page.
                         "parking"],
    features_amenities: ["furnishing", "facing", "property_age", "amenities"],
    price_charges:      ["price", "monthly_rent", "monthly_lease", "security_deposit", "maintenance", "negotiable", "ownership", "possession"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
  House: {
    basic:              COMMON_BASIC,
    rooms_area:         ["bhk", "bathrooms", "balconies", "plot_area", "built_up_area"],
    floor_building:     ["floors_count", "parking"],
    features_amenities: ["furnishing", "facing", "property_age", "road_width", "amenities"],
    price_charges:      ["price", "monthly_rent", "monthly_lease", "security_deposit", "negotiable", "ownership", "possession"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
  Plot: {
    basic:              COMMON_BASIC,
    rooms_area:         ["plot_area", "plot_dimensions"],
    // Plot deliberately shows NO floor/building fields.
    floor_building:     [],
    features_amenities: ["facing", "road_width", "boundary_wall", "corner_property",
                         "approved_authority", "construction_allowed"],
    price_charges:      ["price", "monthly_lease", "negotiable", "ownership"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
  Shop: {
    // v2.91 — Removed `carpet_area` per user directive; size lives in built_up_area only.
    basic:              COMMON_BASIC,
    rooms_area:         ["built_up_area", "washroom"],
    floor_building:     ["current_floor", "parking"],
    features_amenities: ["frontage", "power_backup", "furnishing", "suitable_business_type",
                         "commercial_road_type", "road_width"],
    price_charges:      ["price", "monthly_rent", "monthly_lease", "security_deposit", "maintenance", "negotiable"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
  Office: {
    // v2.91 — Removed `carpet_area`. Built-up Area is the single size field.
    basic:              COMMON_BASIC,
    rooms_area:         ["built_up_area", "cabins", "workstations",
                         "conference_room", "washrooms", "pantry"],
    floor_building:     ["current_floor", "lift", "parking"],
    features_amenities: ["furnishing", "power_backup"],
    price_charges:      ["price", "monthly_rent", "monthly_lease", "security_deposit", "maintenance", "negotiable"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
  Building: {
    basic:              COMMON_BASIC,
    rooms_area:         ["plot_area", "built_up_area", "total_units"],
    floor_building:     ["floors_count", "lift", "parking"],
    features_amenities: ["usage_type", "facing", "property_age", "road_width"],
    price_charges:      ["price", "monthly_lease", "negotiable", "ownership"],
    location:           COMMON_LOCATION,
    description:        COMMON_DESC,
    documents:          COMMON_MEDIA_DOC,
    contact:            COMMON_CONTACT,
  },
};

// -----------------------------------------------------------------------
// Required-field rules
// -----------------------------------------------------------------------
// Two dimensions: (property_type) + (listing_intent).
// Rules union base + intent-specific.
// -----------------------------------------------------------------------
const REQUIRED_BASE = ["listing_type", "title", "property_type", "main_area", "sub_area"];

const REQUIRED_BY_INTENT = {
  Sale:  ["price"],
  Rent:  ["monthly_rent"],
  Lease: ["monthly_lease"],
};

// v2.92 — Consolidation with propertyTypeMatrix.TYPE_REQUIRED_FIELDS.
//
// Historically the schema declared a richer "always-required" list here
// than the Next-button gate (in propertyTypeMatrix). ReviewPreview
// showed extra "— required" markers for fields (e.g. built_up_area,
// plot_area, possession) that the runtime validator did NOT actually
// enforce, confusing users. The v2.92 audit locked the CURRENT
// intended behavior to TYPE_REQUIRED_FIELDS.
//
// This map now MIRRORS TYPE_REQUIRED_FIELDS one-for-one, translated
// from storage keys to schema field IDs:
//
//   Storage key                 →  Schema field ID
//   custom_fields.bathroom       →  bathrooms  (alias-safe reader in
//                                                readFieldValue)
//   custom_fields.floors_count   →  floors_count
//   custom_fields.current_floor  →  current_floor
//   custom_fields.road_width_ft  →  road_width
//   custom_fields.registry_type  →  ownership
//   custom_fields.shop_type      →  commercial_road_type
//   custom_fields.building_type  →  usage_type
//   custom_fields.total_units    →  total_units
//   top.bhk                      →  bhk
//
// Universal step-3 requires (title, price, size, main_area, sub_area,
// listing_intent) come from REQUIRED_BASE + REQUIRED_BY_INTENT.
// Optional new fields (property_age, car_parking, bike_parking,
// parking_type, facing) are intentionally absent from every list.
const REQUIRED_TYPE_EXTRAS = {
  BuilderFloor: {
    always: ["bhk", "bathrooms", "current_floor", "floors_count"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
  House: {
    always: ["bhk", "bathrooms", "floors_count"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
  Plot: {
    always: ["road_width", "ownership"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
  Shop: {
    always: ["commercial_road_type", "road_width", "current_floor"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
  Office: {
    always: ["commercial_road_type", "road_width", "current_floor"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
  Building: {
    always: ["usage_type", "floors_count", "total_units"],
    Sale:   [],
    Rent:   [],
    Lease:  [],
  },
};

export function getRequiredFields(propertyType, listingIntent) {
  const key = getPropertyTypeKey(propertyType);
  if (!key) return new Set(REQUIRED_BASE);
  const intent = listingIntent || "Sale";
  const req = new Set([
    ...REQUIRED_BASE,
    ...(REQUIRED_BY_INTENT[intent] || []),
    ...(REQUIRED_TYPE_EXTRAS[key]?.always || []),
    ...(REQUIRED_TYPE_EXTRAS[key]?.[intent] || []),
  ]);
  return req;
}

// -----------------------------------------------------------------------
// getVisibleFieldIds — flat Set of every field id visible for the type
// (union across all sections). Hidden legacy field values are NEVER
// erased; the form/detail simply doesn't render them.
// -----------------------------------------------------------------------
export function getVisibleFieldIds(propertyType) {
  const key = getPropertyTypeKey(propertyType);
  if (!key) return new Set();
  const byType = FIELDS_BY_TYPE[key];
  if (!byType) return new Set();
  const out = new Set();
  for (const section of SECTION_ORDER) {
    for (const id of byType[section] || []) out.add(id);
  }
  return out;
}

// -----------------------------------------------------------------------
// readFieldValue — helper to read a field's value from a property doc
// respecting `store: "top" | "custom"`. Used by PropertyDetailSections
// + form Review renderer.
//
// v2.91 — Legacy-alias resolver.
// Some concepts live under multiple stored keys because the form and
// the detail-page schema evolved independently. Rather than a
// destructive migration (which would risk existing data), the field
// meta declares `readAliases: [...]` and this function walks them in
// order. First non-empty value wins. Aliases prefixed with "top:"
// resolve against the top-level doc; otherwise they resolve against
// `doc.custom_fields`.
//
// Example: `balconies` (canonical) falls back to `balcony` (legacy
// form key) so old records whose values live under `custom_fields.
// balcony` remain visible under the new "Balconies" label.
// -----------------------------------------------------------------------
function _isEmpty(v) {
  return v === undefined || v === null || v === "" ||
         (Array.isArray(v) && v.length === 0);
}

export function readFieldValue(fieldId, doc) {
  const meta = FIELD_META[fieldId];
  if (!meta || !doc) return undefined;
  const src = meta.store === "custom" ? (doc.custom_fields || {}) : doc;
  const primary = src[meta.storeKey];
  if (!_isEmpty(primary)) return primary;
  // Walk read-only aliases in declared order — first hit wins.
  const aliases = Array.isArray(meta.readAliases) ? meta.readAliases : [];
  for (const alias of aliases) {
    let v;
    if (typeof alias === "string" && alias.startsWith("top:")) {
      v = doc[alias.slice(4)];
    } else {
      v = (doc.custom_fields || {})[alias];
    }
    if (!_isEmpty(v)) return v;
  }
  return primary;   // return the (empty) primary so callers can distinguish undefined
}

// -----------------------------------------------------------------------
// resolveParkingSummary — v2.93 canonical-first parking read helper.
//
// v2.91 briefly used a split model (car_parking + bike_parking +
// parking_type). v2.93 collapsed it back to a single `custom_fields.
// parking` value with 4 options: None / Car / Bike / Car + Bike.
//
// Read-time priority (highest → lowest):
//   1. canonical `custom_fields.parking` present   → mode = "canonical"
//   2. derive from `car_parking` + `bike_parking`  → mode = "derived"
//      (legacy Yes/No split — normalise into Car / Bike / Car + Bike /
//       None per the truth table below)
//   3. nothing set                                 → mode = "empty"
//
// `custom_fields.parking_type` (Covered / Stilt) is DELIBERATELY
// ignored for display — it never produces a public row on new listings,
// and on legacy records it would only duplicate the parking concept.
// The data itself is preserved untouched.
//
// Truth table for the derived path:
//   car=Yes,  bike=Yes → "Car + Bike"
//   car=Yes,  bike≠Yes → "Car"
//   car≠Yes,  bike=Yes → "Bike"
//   otherwise         → "None"        (both absent OR both explicit "No")
//
// Returned shape (stable — the detail-page renderer branches on
// `.value` and displays exactly one row):
//   { mode: "canonical"|"derived"|"empty",
//     value: "None"|"Car"|"Bike"|"Car + Bike"|"",
//     legacy_type: string    // preserved for admin diagnostics only
//   }
// -----------------------------------------------------------------------

// v2.93 — normalise legacy vehicle-based labels + open labels to the new
// 4-option enum. Used by the resolver so old records display cleanly.
const _PARKING_ALIAS_MAP = {
  // pre-v2.93 vehicle labels → new labels
  "No Parking": "None",
  "Car Parking": "Car",
  "Bike Parking": "Bike",
  "Car + Bike Parking": "Car + Bike",
  // pre-v2.83 open labels — best-effort mapping. "Open"/"Covered"/"Both"
  // don't carry a vehicle distinction, so we surface them as-is for the
  // admin to eyeball on legacy records; the detail renderer will show
  // the original string. Callers that need a strict enum should treat
  // anything not in {None, Car, Bike, Car + Bike} as opaque legacy.
};

function _normaliseParkingValue(raw) {
  if (_isEmpty(raw)) return "";
  const s = String(raw).trim();
  return _PARKING_ALIAS_MAP[s] || s;
}

export function resolveParkingSummary(doc) {
  const cf = (doc && doc.custom_fields) || {};
  const canonical = cf.parking;
  const car = cf.car_parking;
  const bike = cf.bike_parking;
  const type = cf.parking_type;

  // (1) Canonical wins — even when legacy split fields also happen to
  //     exist. This matches the spec: "If an existing canonical
  //     `parking` value exists, it should take priority over legacy
  //     split fields."
  if (!_isEmpty(canonical)) {
    return {
      mode: "canonical",
      value: _normaliseParkingValue(canonical),
      legacy_type: _isEmpty(type) ? "" : String(type),
    };
  }

  // (2) Derive from split Yes/No pair. Empty strings + false-y values
  //     count as "not yes". "No" is a valid entered value — result
  //     resolves to "None" only when BOTH are non-yes.
  const carYes  = String(car  || "").trim().toLowerCase() === "yes";
  const bikeYes = String(bike || "").trim().toLowerCase() === "yes";
  const hasAnySplit = !_isEmpty(car) || !_isEmpty(bike);
  if (hasAnySplit) {
    let value = "None";
    if (carYes && bikeYes) value = "Car + Bike";
    else if (carYes) value = "Car";
    else if (bikeYes) value = "Bike";
    return {
      mode: "derived",
      value,
      legacy_type: _isEmpty(type) ? "" : String(type),
    };
  }

  // (3) Nothing to show. `parking_type` alone is intentionally treated
  //     as no info — Covered/Stilt without a vehicle context is not
  //     meaningful to public viewers.
  return {
    mode: "empty",
    value: "",
    legacy_type: _isEmpty(type) ? "" : String(type),
  };
}

// -----------------------------------------------------------------------
// Field id → containing section for a given property type. Used by the
// Review step to render fields in section order.
// -----------------------------------------------------------------------
export function sectionsForType(propertyType) {
  const key = getPropertyTypeKey(propertyType);
  if (!key) return [];
  const byType = FIELDS_BY_TYPE[key] || {};
  return SECTION_ORDER.map((sec) => ({
    id: sec,
    label: SECTION_LABELS[sec],
    fields: byType[sec] || [],
  })).filter((s) => s.fields.length > 0);
}
